import { GetUserDataResult } from "@/lib/types";
import { SubscriptionStatus } from "@/lib/types";
import {
  ParentStudentInvite,
  ParentSubscription,
  SubscriptionLink
} from "@/lib/parent_student_types";

/* ═══════════════════════════════════════════════
   PRESET DEFINITIONS — shared between mock routes
   and preview page UI.

   Each preset represents a distinct visual state
   that produces different UI across the app.
   ═══════════════════════════════════════════════ */

export type DevPresetKey =
  // General
  | "none"
  | "not_logged_in"
  // Student — no subscription
  | "student_no_sub"
  // Student — trial
  | "student_trial"
  | "student_trial_ending"
  | "student_trial_expired"
  // Student — active subscription
  | "student_active_sub"
  | "student_second_child"
  | "student_active_sub_linked"
  // Student — problem states
  | "student_canceled_sub"
  | "student_pending_sub"
  | "student_chargeback_md06"
  | "student_chargeback_am04"
  | "student_chargeback_ms02"
  | "student_chargeback_ac06"
  | "student_chargeback_sl01"
  // Student — school/organization
  | "student_unlimited"
  | "student_inactive_organization"
  | "student_active_organization"
  // Parent
  | "parent_trial_expired"
  | "parent_no_sub"
  | "parent_open_invite"
  | "parent_with_sub"
  | "parent_canceled_sub"
  | "parent_past_due"
  | "parent_pending_sub"
  | "parent_trial"
  | "parent_chargeback_ms02"
  | "parent_chargeback_ac06"
  | "parent_chargeback_sl01";

export interface DevPreset {
  label: string;
  /** null means 401 (not logged in), undefined means no override */
  userData: GetUserDataResult | null | undefined;
  invites: ParentStudentInvite[];
  links: SubscriptionLink[];
  parentSubscription?: ParentSubscription;
}

function makeUser(
  overrides: Partial<GetUserDataResult> = {}
): GetUserDataResult {
  return {
    created_at: "2024-01-01T00:00:00Z",
    first_name: "Test",
    last_name: overrides.role === "parent" ? "Ouder" : "Student",
    email: overrides.email ?? "test@dev.local",
    full_name: overrides.role === "parent" ? "Test Ouder" : "Test Student",
    role: overrides.role ?? "student",
    is_premium: overrides.is_premium ?? false,
    tour_status: overrides.tour_status ?? "not_started",
    unlimited_access: overrides.unlimited_access ?? false,
    free_trial: overrides.free_trial ?? false,
    trial_expired: overrides.trial_expired ?? false,
    subscriptions: overrides.subscriptions ?? [],
    pending_subscription: overrides.pending_subscription ?? false,
    ...overrides
  };
}

function daysFromNow(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

/** Fake unredeemed invite for dev preview (avoids POST to real backend) */
const fakeInvite: ParentStudentInvite = {
  id: 1,
  invite_token: "dev_preview_token",
  inviter_email: "test@dev.local",
  inviter_name: "Test Student",
  expires_at: daysFromNow(7),
  redeemed_at: undefined,
  created_at: new Date().toISOString()
};

export const DEV_PRESETS: Record<DevPresetKey, DevPreset> = {
  // ─── General ───────────────────────────────────
  none: {
    label: "Geen override",
    userData: undefined,
    invites: [],
    links: []
  },
  not_logged_in: {
    label: "Niet ingelogd",
    userData: null,
    invites: [],
    links: []
  },

  // ─── Student: geen abonnement ──────────────────
  student_no_sub: {
    label: "Student (geen abo)",
    userData: makeUser({ is_premium: false }),
    invites: [fakeInvite],
    links: []
  },

  // ─── Student: proefperiode ─────────────────────
  student_trial: {
    label: "Student (proefweek, 7d)",
    userData: makeUser({
      is_premium: true,
      free_trial: true,
      trial_end: daysFromNow(7)
    }),
    invites: [],
    links: []
  },
  student_trial_ending: {
    label: "Student (proefweek, 2d)",
    userData: makeUser({
      is_premium: true,
      free_trial: true,
      trial_end: daysFromNow(2)
    }),
    invites: [],
    links: []
  },
  student_trial_expired: {
    label: "Student (proef verlopen)",
    userData: makeUser({ trial_expired: true }),
    invites: [fakeInvite],
    links: []
  },

  // ─── Student: actief abonnement ────────────────
  student_active_sub: {
    label: "Student (actief abo)",
    userData: makeUser({
      is_premium: true,
      subscriptions: [
        {
          id: "sub_dev_active",
          status: SubscriptionStatus.active,
          next_payment_date: daysFromNow(30)
        }
      ]
    }),
    invites: [],
    links: []
  },
  student_second_child: {
    label: "Student (tweede kind, €60/jaar)",
    userData: makeUser({
      is_premium: true,
      subscriptions: [{
        id: "sub_dev_second_child",
        status: SubscriptionStatus.active,
        plan_id: "yearly_second_child",
        next_payment_date: daysFromNow(365)
      }]
    }),
    invites: [],
    links: []
  },
  student_active_sub_linked: {
    label: "Student (abo via ouder)",
    userData: makeUser({
      is_premium: true,
      subscriptions: [
        {
          id: "sub_dev_linked",
          status: SubscriptionStatus.active,
          next_payment_date: daysFromNow(30),
          valid_until: daysFromNow(30),
          link_details: {
            linked_email: "ouder@dev.local",
            linked_full_name: "Test Ouder"
          }
        }
      ]
    }),
    invites: [],
    links: [
      {
        id: 1,
        parent_email: "ouder@dev.local",
        student_email: "test@dev.local",
        student_name: "Test Student",
        subscription_status: SubscriptionStatus.active,
        subscription_id: "sub_dev_linked"
      }
    ]
  },

  // ─── Student: probleem states ──────────────────
  student_canceled_sub: {
    label: "Student (opgezegd)",
    userData: makeUser({
      is_premium: true,
      subscriptions: [
        {
          id: "sub_dev_canceled",
          status: SubscriptionStatus.canceled,
          valid_until: daysFromNow(14)
        }
      ]
    }),
    invites: [],
    links: []
  },
  student_pending_sub: {
    label: "Student (pending)",
    userData: makeUser({
      pending_subscription: true,
      subscriptions: [
        {
          id: "sub_dev_pending",
          status: SubscriptionStatus.pending
        }
      ]
    }),
    invites: [],
    links: []
  },
  student_chargeback_md06: {
    label: "Student (chargeback MD06)",
    userData: makeUser({
      subscriptions: [
        {
          id: "sub_dev_chargeback",
          status: SubscriptionStatus.canceled,
          chargeback_status: {
            payment_created_at: "2024-06-01T00:00:00Z",
            charged_back_at: "2024-06-15T00:00:00Z",
            code: "MD06"
          }
        }
      ]
    }),
    invites: [],
    links: []
  },
  student_chargeback_am04: {
    label: "Student (chargeback AM04)",
    userData: makeUser({
      subscriptions: [
        {
          id: "sub_dev_chargeback_am04",
          status: SubscriptionStatus.active,
          next_payment_date: daysFromNow(14),
          chargeback_status: {
            payment_created_at: "2024-06-01T00:00:00Z",
            charged_back_at: "2024-06-15T00:00:00Z",
            code: "AM04"
          }
        }
      ]
    }),
    invites: [],
    links: []
  },
  student_chargeback_ms02: {
    label: "Student (chargeback MS02)",
    userData: makeUser({
      subscriptions: [
        {
          id: "sub_dev_chargeback_ms02",
          status: SubscriptionStatus.canceled,
          valid_until: daysFromNow(-1),
          chargeback_status: {
            payment_created_at: "2024-06-01T00:00:00Z",
            charged_back_at: "2024-06-15T00:00:00Z",
            code: "MS02"
          }
        }
      ]
    }),
    invites: [],
    links: []
  },
  student_chargeback_ac06: {
    label: "Student (chargeback AC06)",
    userData: makeUser({
      subscriptions: [
        {
          id: "sub_dev_chargeback_ac06",
          status: SubscriptionStatus.canceled,
          valid_until: daysFromNow(-1),
          chargeback_status: {
            payment_created_at: "2024-06-01T00:00:00Z",
            charged_back_at: "2024-06-15T00:00:00Z",
            code: "AC06"
          }
        }
      ]
    }),
    invites: [],
    links: []
  },
  student_chargeback_sl01: {
    label: "Student (chargeback SL01)",
    userData: makeUser({
      subscriptions: [
        {
          id: "sub_dev_chargeback_sl01",
          status: SubscriptionStatus.canceled,
          valid_until: daysFromNow(-1),
          chargeback_status: {
            payment_created_at: "2024-06-01T00:00:00Z",
            charged_back_at: "2024-06-15T00:00:00Z",
            code: "SL01"
          }
        }
      ]
    }),
    invites: [],
    links: []
  },

  student_inactive_organization: {
    label: "Student (inactive school)",
    userData: makeUser({
      organization: "Test School",
      organization_access_active: false
    }),
    invites: [],
    links: []
  },
  student_active_organization: {
    label: "Student (active school)",
    userData: makeUser({
      organization: "Test School",
      organization_access_active: true,
      is_premium: true
    }),
    invites: [],
    links: []
  },
  // ─── Student: school/organisatie ────────────────
  student_unlimited: {
    label: "Student (school/unlimited)",
    userData: makeUser({
      is_premium: true,
      unlimited_access: true,
      organization: "Test School"
    }),
    invites: [],
    links: []
  },

  // ─── Ouder ─────────────────────────────────────
  parent_trial_expired: {
    label: "Ouder (proef verlopen)",
    userData: makeUser({ role: "parent", trial_expired: true }),
    invites: [],
    links: []
  },
  parent_no_sub: {
    label: "Ouder (geen abo)",
    userData: makeUser({ role: "parent" }),
    invites: [],
    links: []
  },
  parent_open_invite: {
    label: "Ouder (open uitnodiging)",
    userData: makeUser({
      role: "parent",
      is_premium: true,
      subscriptions: [
        {
          id: "sub_dev_parent_open",
          status: SubscriptionStatus.active,
          next_payment_date: daysFromNow(30)
        }
      ]
    }),
    invites: [
      {
        id: 21,
        created_at: "2024-01-10T00:00:00Z",
        invite_token: "dev-invite-token-parent-open",
        inviter_email: "test@dev.local",
        inviter_role: "parent",
        subscription_id: "sub_dev_parent_open"
      }
    ],
    links: [],
    parentSubscription: {
      id: "sub_dev_parent_open",
      status: SubscriptionStatus.active,
      next_payment_date: daysFromNow(30),
      last_payment: daysFromNow(-1)
    }
  },
  parent_with_sub: {
    label: "Ouder (actief abo)",
    userData: makeUser({
      role: "parent",
      is_premium: true,
      subscriptions: [
        {
          id: "sub_dev_parent_active",
          status: SubscriptionStatus.active,
          next_payment_date: daysFromNow(30),
          link_details: {
            linked_email: "kind@dev.local",
            linked_full_name: "Test Kind"
          }
        }
      ]
    }),
    invites: [
      {
        id: 1,
        created_at: "2024-01-10T00:00:00Z",
        invite_token: "dev-invite-token-parent-active",
        inviter_email: "test@dev.local",
        inviter_role: "parent",
        redeemed_at: "2024-01-15T00:00:00Z",
        subscription_id: "sub_dev_parent_active"
      }
    ],
    links: [
      {
        id: 1,
        parent_email: "test@dev.local",
        student_email: "kind@dev.local",
        student_name: "Test Kind",
        subscription_status: SubscriptionStatus.active,
        subscription_id: "sub_dev_parent_active"
      }
    ],
    parentSubscription: {
      id: "sub_dev_parent_active",
      status: SubscriptionStatus.active,
      next_payment_date: daysFromNow(30),
      last_payment: daysFromNow(-1),
      linked_student_info: {
        first_name: "Test",
        last_name: "Kind",
        full_name: "Test Kind",
        email: "kind@dev.local"
      }
    }
  },
  parent_canceled_sub: {
    label: "Ouder (opgezegd)",
    userData: makeUser({
      role: "parent",
      is_premium: true,
      subscriptions: [
        {
          id: "sub_dev_parent_canceled",
          status: SubscriptionStatus.canceled,
          valid_until: daysFromNow(14),
          link_details: {
            linked_email: "kind@dev.local",
            linked_full_name: "Test Kind"
          }
        }
      ]
    }),
    invites: [
      {
        id: 2,
        created_at: "2024-01-10T00:00:00Z",
        invite_token: "dev-invite-token-parent-canceled",
        inviter_email: "test@dev.local",
        inviter_role: "parent",
        redeemed_at: "2024-01-15T00:00:00Z",
        subscription_id: "sub_dev_parent_canceled"
      }
    ],
    links: [
      {
        id: 2,
        parent_email: "test@dev.local",
        student_email: "kind@dev.local",
        student_name: "Test Kind",
        subscription_status: SubscriptionStatus.canceled,
        subscription_id: "sub_dev_parent_canceled"
      }
    ],
    parentSubscription: {
      id: "sub_dev_parent_canceled",
      status: SubscriptionStatus.canceled,
      valid_until: daysFromNow(14),
      last_payment: daysFromNow(-15),
      canceled_at: daysFromNow(-1),
      linked_student_info: {
        first_name: "Test",
        last_name: "Kind",
        full_name: "Test Kind",
        email: "kind@dev.local"
      }
    }
  },
  parent_past_due: {
    label: "Ouder (betalingsprobleem)",
    userData: makeUser({
      role: "parent",
      is_premium: true,
      subscriptions: [
        {
          id: "sub_dev_parent_past_due",
          status: SubscriptionStatus.past_due,
          valid_until: daysFromNow(5),
          link_details: {
            linked_email: "kind@dev.local",
            linked_full_name: "Test Kind"
          }
        }
      ]
    }),
    invites: [
      {
        id: 3,
        created_at: "2024-01-10T00:00:00Z",
        invite_token: "dev-invite-token-parent-past-due",
        inviter_email: "test@dev.local",
        inviter_role: "parent",
        redeemed_at: "2024-01-15T00:00:00Z",
        subscription_id: "sub_dev_parent_past_due"
      }
    ],
    links: [
      {
        id: 3,
        parent_email: "test@dev.local",
        student_email: "kind@dev.local",
        student_name: "Test Kind",
        subscription_status: SubscriptionStatus.past_due,
        subscription_id: "sub_dev_parent_past_due",
        chargeback: false
      }
    ],
    parentSubscription: {
      id: "sub_dev_parent_past_due",
      status: SubscriptionStatus.past_due,
      valid_until: daysFromNow(5),
      last_payment: daysFromNow(-25),
      linked_student_info: {
        first_name: "Test",
        last_name: "Kind",
        full_name: "Test Kind",
        email: "kind@dev.local"
      }
    }
  },
  parent_pending_sub: {
    label: "Ouder (pending betaling)",
    userData: makeUser({
      role: "parent",
      pending_subscription: true
    }),
    invites: [],
    links: []
  },
  parent_trial: {
    label: "Ouder (proefweek, invite ingetrokken)",
    userData: makeUser({
      role: "parent",
      is_premium: true,
      free_trial: true,
      trial_end: daysFromNow(7),
      subscriptions: []
    }),
    invites: [],
    links: []
  },
  parent_chargeback_ms02: {
    label: "Ouder (chargeback MS02)",
    userData: makeUser({
      role: "parent",
      is_premium: true,
      subscriptions: [
        {
          id: "sub_dev_parent_chargeback_ms02",
          status: SubscriptionStatus.canceled,
          valid_until: daysFromNow(-1),
          chargeback_status: {
            payment_created_at: "2024-06-01T00:00:00Z",
            charged_back_at: "2024-06-15T00:00:00Z",
            code: "MS02"
          },
          link_details: {
            linked_email: "kind@dev.local",
            linked_full_name: "Test Kind"
          }
        }
      ]
    }),
    invites: [],
    links: [
      {
        id: 10,
        parent_email: "test@dev.local",
        student_email: "kind@dev.local",
        student_name: "Test Kind",
        subscription_status: SubscriptionStatus.canceled,
        subscription_id: "sub_dev_parent_chargeback_ms02",
        chargeback: true
      }
    ],
    parentSubscription: {
      id: "sub_dev_parent_chargeback_ms02",
      status: SubscriptionStatus.canceled,
      valid_until: daysFromNow(-1),
      last_payment: daysFromNow(-15),
      canceled_at: daysFromNow(-1),
      chargeback_status: {
        payment_created_at: "2024-06-01T00:00:00Z",
        charged_back_at: "2024-06-15T00:00:00Z",
        code: "MS02"
      },
      linked_student_info: {
        first_name: "Test",
        last_name: "Kind",
        full_name: "Test Kind",
        email: "kind@dev.local"
      }
    }
  },
  parent_chargeback_ac06: {
    label: "Ouder (chargeback AC06)",
    userData: makeUser({
      role: "parent",
      is_premium: true,
      subscriptions: [
        {
          id: "sub_dev_parent_chargeback_ac06",
          status: SubscriptionStatus.canceled,
          valid_until: daysFromNow(-1),
          chargeback_status: {
            payment_created_at: "2024-06-01T00:00:00Z",
            charged_back_at: "2024-06-15T00:00:00Z",
            code: "AC06"
          },
          link_details: {
            linked_email: "kind@dev.local",
            linked_full_name: "Test Kind"
          }
        }
      ]
    }),
    invites: [],
    links: [
      {
        id: 11,
        parent_email: "test@dev.local",
        student_email: "kind@dev.local",
        student_name: "Test Kind",
        subscription_status: SubscriptionStatus.canceled,
        subscription_id: "sub_dev_parent_chargeback_ac06",
        chargeback: true
      }
    ],
    parentSubscription: {
      id: "sub_dev_parent_chargeback_ac06",
      status: SubscriptionStatus.canceled,
      valid_until: daysFromNow(-1),
      last_payment: daysFromNow(-15),
      canceled_at: daysFromNow(-1),
      chargeback_status: {
        payment_created_at: "2024-06-01T00:00:00Z",
        charged_back_at: "2024-06-15T00:00:00Z",
        code: "AC06"
      },
      linked_student_info: {
        first_name: "Test",
        last_name: "Kind",
        full_name: "Test Kind",
        email: "kind@dev.local"
      }
    }
  },
  parent_chargeback_sl01: {
    label: "Ouder (chargeback SL01)",
    userData: makeUser({
      role: "parent",
      is_premium: true,
      subscriptions: [
        {
          id: "sub_dev_parent_chargeback_sl01",
          status: SubscriptionStatus.canceled,
          valid_until: daysFromNow(-1),
          chargeback_status: {
            payment_created_at: "2024-06-01T00:00:00Z",
            charged_back_at: "2024-06-15T00:00:00Z",
            code: "SL01"
          },
          link_details: {
            linked_email: "kind@dev.local",
            linked_full_name: "Test Kind"
          }
        }
      ]
    }),
    invites: [],
    links: [
      {
        id: 12,
        parent_email: "test@dev.local",
        student_email: "kind@dev.local",
        student_name: "Test Kind",
        subscription_status: SubscriptionStatus.canceled,
        subscription_id: "sub_dev_parent_chargeback_sl01",
        chargeback: true
      }
    ],
    parentSubscription: {
      id: "sub_dev_parent_chargeback_sl01",
      status: SubscriptionStatus.canceled,
      valid_until: daysFromNow(-1),
      last_payment: daysFromNow(-15),
      canceled_at: daysFromNow(-1),
      chargeback_status: {
        payment_created_at: "2024-06-01T00:00:00Z",
        charged_back_at: "2024-06-15T00:00:00Z",
        code: "SL01"
      },
      linked_student_info: {
        first_name: "Test",
        last_name: "Kind",
        full_name: "Test Kind",
        email: "kind@dev.local"
      }
    }
  }
};
